import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewAccessRequest } from '@/services/workflow/manage-access-requests';
import { NOTIFICATIONS_QUERY_KEY } from '@/hooks/queries/use-get-notifications';
import { AccessRequestDecision } from '@/types/share-types';

interface ReviewAccessRequestArgs {
  orgId: string;
  workflowId: string;
  requestId: string;
  decision: AccessRequestDecision;
}

export class ReviewAccessRequestError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ReviewAccessRequestError';
    this.statusCode = statusCode;
  }
}

export const useReviewAccessRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      workflowId,
      requestId,
      decision,
    }: ReviewAccessRequestArgs) => {
      const res = (await reviewAccessRequest(
        orgId,
        workflowId,
        requestId,
        decision,
        decision === 'approved' ? 'workflow:editor' : undefined,
      )) as { success?: boolean; statusCode?: number; detail?: string };
      if (res?.success === false || (res?.statusCode ?? 0) >= 400) {
        throw new ReviewAccessRequestError(
          res?.detail || 'Failed to review request',
          res?.statusCode,
        );
      }
      return res;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });
};
